import { type MouseEvent, memo, useCallback } from 'react';

import { workspaceAgentMentionFilterChip } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMentionFilterChipChrome';
import { type WorkspaceAgentMentionSourceKey } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';

export type WorkspaceAgentMentionFilterChipProps = {
  /** Set when the visible label is not the whole name — the brand kit chip shows the kit's name. */
  ariaLabel?: string;
  /** Chrome the caller adds, for a chip that is one half of a wider control. */
  className?: string;
  isActive: boolean;
  label: string;
  onSelect: (sourceKey: WorkspaceAgentMentionSourceKey) => void;
  sourceKey: WorkspaceAgentMentionSourceKey;
};

export const WorkspaceAgentMentionFilterChip = memo(
  ({ ariaLabel, className, isActive, label, onSelect, sourceKey }: WorkspaceAgentMentionFilterChipProps) => {
    const handleClick = useCallback(() => {
      onSelect(sourceKey);
    }, [onSelect, sourceKey]);

    /** A mousedown here would blur the editor. */
    const handleMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    }, []);

    return (
      <button
        aria-label={ariaLabel}
        aria-pressed={isActive}
        className={workspaceAgentMentionFilterChip({ className, isActive })}
        data-mention-source={sourceKey}
        data-testid="WORKSPACE_AGENT_MENTION_FILTER_CHIP"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        /** Focus stays in the composer while the picker is open. */
        tabIndex={-1}
        /**
         * A native title, not a `Tooltip`: tooltip content portals outside this popover, so a press
         * on it reads as an outside press and would dismiss the picker. On the button, not the
         * truncated label — the whole chip is the hover target.
         */
        title={label}
        type="button"
      >
        <span className="truncate">{label}</span>
      </button>
    );
  },
);

WorkspaceAgentMentionFilterChip.displayName = 'WorkspaceAgentMentionFilterChip';
